/* Modulo: Slideshow Glass — full-bleed slideshow with a frosted content card
   and a server-rendered thumbnail navigation plate.

   JS owns the index; CSS owns the reveal, so there is zero CLS. Thumbnails and
   the counter are real markup — this file only syncs their state, it never
   builds them. Also: arrows, keyboard, pointer-swipe, autoplay (pauses on
   hover/focus and via a persistent control), lazy <picture> hydration, and
   background-video playback that is gated on reduced motion, tab visibility and
   whether the section is actually on screen.

   STRUCTURE: the video subsystem is set up BEFORE the "fewer than two slides"
   early return, because a single-slide hero can carry a video and that video
   renders with autoplay off and its pause control hidden — bail early and it is
   a frozen poster with nothing on the page able to start it. Only the carousel
   machinery (index, thumbnails, arrows, swipe, autoplay) is behind that return.

   Idempotent init, theme-editor reload safe, leak-proof. Vanilla IIFE, no deps. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var REDUCE = !!(mq && mq.matches);

  /* Pause-on-hover only where hover exists. On touch a tap emits a synthetic
     mouseenter with no guaranteed mouseleave, which latches `hovering` true and
     makes canAuto() refuse every later start() — autoplay dead after one tap.
     (slideshow-classic hit exactly this.) */
  var HOVER = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  var SWIPE = 50;
  var SLIDE_MS = 880;
  var FADE_MS = 620;
  var QUICK_MS = 220;

  function setSlideState(slide, active) {
    if (active) slide.removeAttribute('aria-hidden');
    else slide.setAttribute('aria-hidden', 'true');
    if ('inert' in slide) slide.inert = !active;
    var ctrls = slide.querySelectorAll('a, button');
    for (var i = 0; i < ctrls.length; i++) {
      if (active) ctrls[i].removeAttribute('tabindex');
      else ctrls[i].setAttribute('tabindex', '-1');
    }
  }

  function pad2(n) { return n < 10 ? '0' + n : String(n); }

  /* Reduced motion suppresses auto-advance, which is correct, but it is
     completely invisible: a merchant with "Reduce motion" on their own machine
     turns autoplay on, watches a hero that never rotates, and reasonably
     concludes the section is broken. Say so, in the theme editor only, where
     the belief is formed. Built with textContent, never innerHTML. */
  function reduceMotionNotice(root, wantsAuto) {
    if (!wantsAuto || !REDUCE) return;
    if (!window.Shopify || !window.Shopify.designMode) return;
    if (root.querySelector('[data-modulo-slg-rm-notice]')) return;

    var note = document.createElement('p');
    note.setAttribute('data-modulo-slg-rm-notice', '');
    note.textContent =
      'Autoplay is paused because "Reduce motion" is turned on in this device\'s ' +
      'accessibility settings. Shoppers without that setting still see the slides ' +
      'rotate. To rotate for them too, turn on "Also rotate for reduced-motion ' +
      'visitors" under Slideshow behavior. Only you can see this note, and only in ' +
      'the theme editor.';
    note.style.cssText =
      'margin:0 0 12px;padding:10px 14px;border-radius:8px;background:#FEF3C7;' +
      'color:#7C2D12;border:1px solid #FCD34D;font-family:system-ui,-apple-system,' +
      'sans-serif;font-size:13px;font-weight:500;line-height:1.45;text-align:left;';
    root.insertBefore(note, root.firstChild);
  }

  function initSlideshow(root) {
    if (root.dataset.moduloSlgReady === '1') return;   // idempotent
    root.dataset.moduloSlgReady = '1';

    var stage  = root.querySelector('[data-stage]');
    var slides = root.querySelectorAll('[data-slide]');
    var total  = slides.length;
    if (!stage || total < 1) return;

    var loop       = root.dataset.loop === '1';
    var wantsAuto  = root.dataset.autoplay === '1';
    var interval   = (parseInt(root.dataset.interval, 10) || 6) * 1000;
    var transition = root.dataset.transition || 'slide';

    var liveRegion = root.querySelector('[data-live]');
    var thumbs     = root.querySelectorAll('[data-thumb]');
    var counter    = root.querySelector('[data-counter]');
    var prevBtns   = root.querySelectorAll('[data-prev]');
    var nextBtns   = root.querySelectorAll('[data-next]');
    var pauseBtn   = root.querySelector('[data-playpause]');

    var current = 0;
    var animating = false;
    var timer = null;
    var clearTimer = null;
    var userPaused = false;
    var hovering = false;
    var io = null;

    /* Starts false so a section below the fold never gets a free play before the
       observer has had its say. With no IntersectionObserver there is nothing to
       promote it later, so assume visible in that case. */
    var onScreen = !window.IntersectionObserver;

    var allowReducedAuto = root.dataset.autoplayReducedMotion === '1';
    function autoBlocked() { return REDUCE && !allowReducedAuto; }

    /* ── Background video ──────────────────────────────────────────────────
       The markup ships autoplay OFF and the control ships hidden, so with no JS
       there is no motion and nothing pretending to stop it. Playback starts
       only when the slide is current, the section is on screen, the tab is
       visible, reduced motion is off, and the shopper has not paused it. */
    function videoOf(slide) { return slide ? slide.querySelector('video') : null; }

    function syncVidBtn(slide) {
      var v = videoOf(slide);
      var btn = slide ? slide.querySelector('[data-vidbtn]') : null;
      if (!v || !btn) return;
      var playing = !v.paused && !v.ended;
      btn.classList.toggle('is-paused', !playing);
      btn.setAttribute('aria-label', playing ? 'Pause background video' : 'Play background video');
      var lbl = btn.querySelector('[data-vidbtn-label]');
      if (lbl) lbl.textContent = playing ? 'Pause' : 'Play';
    }

    /* Reveal/hide must be re-run whenever REDUCE changes, not decided once at
       init: a shopper who turns "Reduce motion" OFF mid-session would otherwise
       get a looping background video with its stop control still hidden — the
       exact WCAG 2.2.2 failure the control exists to prevent. The inverse leaks
       too: turning it ON leaves a visible button whose play path now bails. */
    function syncVidControls() {
      for (var i = 0; i < total; i++) {
        var btn = slides[i].querySelector('[data-vidbtn]');
        if (!btn || !videoOf(slides[i])) continue;
        if (REDUCE) btn.setAttribute('hidden', '');
        else btn.removeAttribute('hidden');
      }
    }

    function canPlayVideo(v) {
      return !REDUCE && onScreen && !document.hidden && v.dataset.slgUserPaused !== '1';
    }

    function playVideo(slide) {
      var v = videoOf(slide);
      if (!v || !canPlayVideo(v)) return;
      var p = v.play();
      /* A rejected play() is normal (autoplay policy, detached element). Swallow
         it — the 'pause'/'play' listeners keep the control honest either way. */
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    function pauseAllVideos(except) {
      for (var i = 0; i < total; i++) {
        if (slides[i] === except) continue;
        var v = videoOf(slides[i]);
        if (v && !v.paused) v.pause();
      }
    }

    /* Decide FIRST, then choose what to exempt. Exempting the current slide
       unconditionally meant the one video JS ever starts was precisely the one
       the pause loop was told to skip, so the off-screen gate could never stop
       it — a hero video kept decoding for the whole session once the shopper
       scrolled past. The exemption is only there to avoid a pause/play flicker
       when the current slide should keep playing. */
    function syncVideos() {
      var cur = slides[current];
      var v = videoOf(cur);
      var allow = !!(v && canPlayVideo(v));
      pauseAllVideos(allow ? cur : null);
      if (allow) playVideo(cur);
    }

    for (var vi = 0; vi < total; vi++) {
      (function (slide) {
        var v = videoOf(slide);
        var btn = slide.querySelector('[data-vidbtn]');
        if (!v || !btn) return;

        v.addEventListener('play', function () { syncVidBtn(slide); });
        v.addEventListener('pause', function () { syncVidBtn(slide); });
        btn.addEventListener('click', function () {
          if (v.paused) {
            v.dataset.slgUserPaused = '0';
            playVideo(slide);
          } else {
            v.dataset.slgUserPaused = '1';
            v.pause();
          }
        });
        syncVidBtn(slide);
      })(slides[vi]);
    }
    syncVidControls();

    /* ── Autoplay state machine ────────────────────────────────────────────
       Runs only when wanted, motion is allowed, the shopper hasn't paused it,
       and the pointer isn't hovering. start() is the single gate, and it is
       inert on a single-slide install because canAuto() requires total >= 2. */
    function canAuto() {
      return wantsAuto && !autoBlocked() && !userPaused && !hovering && total >= 2 && root.isConnected;
    }
    function start() { if (!canAuto()) return; stop(); timer = setInterval(autoNext, interval); }
    function stop()  { if (timer) { clearInterval(timer); timer = null; } }

    /* Don't burn a decode on a hero nobody is looking at. Also the single place
       `onScreen` is written, so the video gate stays honest. */
    if (window.IntersectionObserver) {
      io = new window.IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].target === root) onScreen = entries[i].isIntersecting;
        }
        syncVideos();
      }, { threshold: 0.15 });
      io.observe(root);
    }

    function onVisibility() {
      if (document.hidden) pauseAllVideos(null);
      else syncVideos();
    }
    document.addEventListener('visibilitychange', onVisibility);

    /* A mid-session reduced-motion toggle must reach all three: the rotation,
       the video, and the video's own control. A load-time boolean cannot. */
    function onMotionChange(e) {
      REDUCE = e.matches;
      syncVidControls();
      if (REDUCE) {
        stop();
        pauseAllVideos(null);
      } else {
        start();
        syncVideos();
      }
    }
    if (mq && mq.addEventListener) mq.addEventListener('change', onMotionChange);

    /* teardown so a theme-editor unload/re-render doesn't leak timers or observers */
    root._slgStop = function () {
      stop();
      clearTimeout(clearTimer);
      pauseAllVideos(null);
      if (io) { io.disconnect(); io = null; }
      document.removeEventListener('visibilitychange', onVisibility);
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onMotionChange);
    };

    syncVideos();

    /* ── Single slide → static hero. Everything below is carousel machinery. ── */
    if (total < 2) return;

    if (wantsAuto && !autoBlocked()) root.classList.add('is-autoplay');
    reduceMotionNotice(root, wantsAuto && !allowReducedAuto);

    for (var s = 0; s < total; s++) {
      if (s !== current) setSlideState(slides[s], false);
    }

    /* ── Lazy <picture> hydration ──────────────────────────────────────────
       Non-first slides ship with data-src/-srcset, making no request until
       promoted. Promote current + neighbours so distant slides never fetch. */
    function hydrate(slide) {
      if (!slide) return;
      var sources = slide.querySelectorAll('source[data-srcset]');
      for (var j = 0; j < sources.length; j++) {
        sources[j].srcset = sources[j].dataset.srcset;
        sources[j].removeAttribute('data-srcset');
      }
      var imgs = slide.querySelectorAll('img[data-src]');
      for (var i = 0; i < imgs.length; i++) {
        var im = imgs[i];
        if (im.dataset.srcset) im.srcset = im.dataset.srcset;
        im.src = im.dataset.src;
        im.removeAttribute('data-src');
        im.removeAttribute('data-srcset');
      }
    }
    function hydrateAround(i) {
      hydrate(slides[i]);
      var nx = i + 1, pv = i - 1;
      if (loop) { nx = nx % total; pv = (pv + total) % total; }
      if (nx < total) hydrate(slides[nx]);
      if (pv >= 0) hydrate(slides[pv]);
    }

    function announce() {
      if (!liveRegion) return;
      var title = slides[current].getAttribute('data-title') || '';
      liveRegion.textContent = 'Slide ' + (current + 1) + ' of ' + total + (title ? ': ' + title : '');
    }

    function toggle(el, off) { off ? el.setAttribute('disabled', '') : el.removeAttribute('disabled'); }

    /* If we are about to disable the arrow that currently holds focus, move
       focus to the opposite arrow (or the stage) so it isn't lost. WCAG 2.4.3. */
    function rescueFocus(group, fallback) {
      for (var i = 0; i < group.length; i++) {
        if (document.activeElement === group[i]) {
          if (fallback && fallback.length && !fallback[0].disabled) fallback[0].focus();
          else if (stage) stage.focus();
        }
      }
    }

    function syncChrome() {
      for (var i = 0; i < thumbs.length; i++) {
        if (i === current) thumbs[i].setAttribute('aria-current', 'true');
        else thumbs[i].removeAttribute('aria-current');
      }
      if (counter) counter.textContent = pad2(current + 1);
      if (!loop) {
        if (current >= total - 1) rescueFocus(nextBtns, prevBtns);
        if (current <= 0) rescueFocus(prevBtns, nextBtns);
        for (var p = 0; p < prevBtns.length; p++) toggle(prevBtns[p], current <= 0);
        for (var n = 0; n < nextBtns.length; n++) toggle(nextBtns[n], current >= total - 1);
      }
    }

    function goTo(index) {
      var target = loop
        ? ((index % total) + total) % total
        : Math.max(0, Math.min(total - 1, index));
      if (target === current || animating) return;

      var outgoing = slides[current];
      var incoming = slides[target];

      hydrate(incoming);
      animating = true;

      outgoing.classList.add('is-leaving');
      outgoing.classList.remove('is-current');
      setSlideState(outgoing, false);

      incoming.classList.add('is-current');
      setSlideState(incoming, true);
      current = target;
      syncChrome();
      announce();
      hydrateAround(current);
      syncVideos();

      var dur = REDUCE ? QUICK_MS : (transition === 'fade' ? FADE_MS : SLIDE_MS);
      clearTimeout(clearTimer);
      clearTimer = setTimeout(function () {
        outgoing.classList.remove('is-leaving');
        animating = false;
      }, dur);
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }
    function autoNext() {
      if (!root.isConnected || autoBlocked()) { stop(); return; }   // self-heal detached/leaked timer
      goTo(loop ? current + 1 : (current + 1) % total);             // autoplay always advances/wraps
    }

    function bind(nl, fn) {
      for (var i = 0; i < nl.length; i++) {
        nl[i].addEventListener('click', function (e) { e.preventDefault(); stop(); fn(); start(); });
      }
    }
    bind(prevBtns, prev);
    bind(nextBtns, next);

    for (var t = 0; t < thumbs.length; t++) {
      (function (idx) {
        thumbs[idx].addEventListener('click', function () { stop(); goTo(idx); start(); });
      })(t);
    }

    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')       { e.preventDefault(); stop(); prev(); start(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); stop(); next(); start(); }
    });

    var sx = 0, dx = 0, drag = false, moved = false;
    stage.addEventListener('pointerdown', function (e) {
      /* Real controls keep their own behaviour. The whole-slide link is the one
         exception: it covers the entire slide, so bailing on it would leave a
         linked slide unswipeable. */
      var hit = e.target.closest('a, button');
      if (hit && !hit.hasAttribute('data-slide-link')) return;
      if (e.button !== undefined && e.button !== 0) return;
      drag = true; moved = false; sx = e.clientX; dx = 0; stop();
    });
    stage.addEventListener('pointermove', function (e) {
      if (!drag) return;
      dx = e.clientX - sx;
      if (Math.abs(dx) > 6) moved = true;
    });

    /* A swipe that starts and ends on the whole-slide link still fires a click.
       Swallow it so swiping changes slides instead of navigating away. */
    stage.addEventListener('click', function (e) {
      if (moved && e.target.closest('[data-slide-link]')) e.preventDefault();
      moved = false;
    }, true);
    function endDrag() {
      if (!drag) return;
      drag = false;
      if (Math.abs(dx) > SWIPE) { dx < 0 ? next() : prev(); }
      dx = 0; start();
    }
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);
    stage.addEventListener('pointerleave', function () { if (drag) endDrag(); });

    if (wantsAuto) {
      if (HOVER) {
        root.addEventListener('mouseenter', function () { hovering = true; stop(); });
        root.addEventListener('mouseleave', function () { hovering = false; start(); });
      }
      // Focus pausing stays on every device: focusout resumes, so it cannot latch.
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
    }

    if (pauseBtn) {
      pauseBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        root.classList.toggle('is-paused', userPaused);
        pauseBtn.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
        if (userPaused) stop(); else start();
      });
    }

    syncChrome();
    hydrateAround(current);
    start();
  }

  function initAll(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-slg]');
    for (var i = 0; i < els.length; i++) initSlideshow(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme editor: re-init on section (re)render, tear down on remove.
     An identical <script src> still executes once per tag, so the document
     listeners are wired exactly once. */
  if (!window.moduloSlgWired) {
    window.moduloSlgWired = true;
    document.addEventListener('shopify:section:load', function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-slg]') : null;
      if (el) initSlideshow(el);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-slg]') : null;
      if (el && typeof el._slgStop === 'function') el._slgStop();
    });
  }
})();
